import { useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import { isPrerenderedFirstLoad } from './prerendered'

/**
 * Whether entry animations are safe to play.
 *
 * Content that starts at `opacity: 0` and is revealed by an animation is
 * invisible for as long as that animation does not run — and it does not run
 * when the page is loaded in a background tab, because the browser pauses the
 * animation frame loop there. Anyone opening the site in a new tab and
 * switching to it later would find a blank page.
 *
 * When this returns false, callers must render the final state directly
 * (`initial={false}`) instead of the hidden one. Nothing on the site should
 * ever depend on an animation to become readable.
 *
 * `trigger` says when the caller animates:
 *  'mount'  (default) plays as soon as the page renders — hero lines. Skipped
 *           on a prerendered first load, where that content is already on
 *           screen and would otherwise blink out and back in.
 *  'scroll' plays when the element scrolls into view (Reveal, SplitHeading).
 *           Still allowed after prerendering: below the fold it has not been
 *           seen yet.
 *
 * Checked once at mount, deliberately: flipping mid-life would restart
 * animations under the visitor.
 */
export const useEntryMotion = (trigger = 'mount') => {
  const reduced = useReducedMotion()
  const [allowed] = useState(() => {
    const pageVisible = typeof document === 'undefined' || document.visibilityState === 'visible'
    const skipForPrerender = trigger === 'mount' && isPrerenderedFirstLoad()
    return pageVisible && !skipForPrerender
  })

  return !reduced && allowed
}

export default useEntryMotion
